import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ErrorOutline,
  Refresh,
  BugReport,
  ExpandMore,
} from '@mui/icons-material';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    // Report error to monitoring service
    if (process.env.NODE_ENV === 'production') {
      this.reportError(error, errorInfo);
    }
  }

  reportError = (error, errorInfo) => {
    // Here you would report to your error monitoring service
    // e.g., Sentry, Bugsnag, or custom logging endpoint
    try {
      const errorReport = {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      };

      // Example: send to monitoring service
      // fetch('/api/errors', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(errorReport),
      // });

      console.log('Error report:', errorReport);
    } catch (reportingError) {
      console.error('Failed to report error:', reportingError);
    }
  };

  handleRefresh = () => {
    // Clear error state and reload
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    });
    
    // Reload the page as last resort
    if (this.props.fallbackReload) {
      window.location.reload();
    }
  };

  handleReportBug = () => {
    const subject = encodeURIComponent(`Bug Report: ${this.state.error?.message || 'Application Error'}`);
    const body = encodeURIComponent(`
Error Details:
- Message: ${this.state.error?.message || 'Unknown error'}
- Stack: ${this.state.error?.stack || 'No stack trace'}
- Component Stack: ${this.state.errorInfo?.componentStack || 'No component stack'}
- URL: ${window.location.href}
- Timestamp: ${new Date().toISOString()}
- User Agent: ${navigator.userAgent}

Steps to reproduce:
1. [Please describe what you were doing when the error occurred]

Expected behavior:
[Please describe what should have happened]

Additional context:
[Any additional information that might help debug this issue]
    `);
    
    const mailtoLink = `mailto:support@kashlegal.com?subject=${subject}&body=${body}`;
    window.open(mailtoLink);
  };

  render() {
    if (this.state.hasError || this.props.error) {
      const error = this.state.error || this.props.error;
      const errorInfo = this.state.errorInfo || {};

      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={this.props.fullScreen ? '100vh' : '400px'}
          p={3}
        >
          <Card sx={{ maxWidth: 600, width: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <ErrorOutline color="error" sx={{ mr: 2, fontSize: 40 }} />
                <Typography variant="h5" component="h1">
                  Something went wrong
                </Typography>
              </Box>

              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>Application Error</AlertTitle>
                {error?.message || 'An unexpected error occurred while loading this component.'}
              </Alert>

              <Typography variant="body2" color="text.secondary" paragraph>
                We apologize for the inconvenience. The error has been logged and will be
                investigated by our development team.
              </Typography>

              <Box display="flex" gap={2} mb={2}>
                <Button
                  variant="contained"
                  startIcon={<Refresh />}
                  onClick={this.handleRefresh}
                >
                  Try Again
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<BugReport />}
                  onClick={this.handleReportBug}
                >
                  Report Bug
                </Button>
              </Box>

              {process.env.NODE_ENV === 'development' && error && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2">
                      Developer Details (Development Only)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      <Typography variant="subtitle2" color="error" gutterBottom>
                        Error Message:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ 
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          backgroundColor: 'grey.100',
                          p: 1,
                          borderRadius: 1,
                          mb: 2,
                        }}
                      >
                        {error.message}
                      </Typography>

                      {error.stack && (
                        <>
                          <Typography variant="subtitle2" color="error" gutterBottom>
                            Stack Trace:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ 
                              fontFamily: 'monospace',
                              whiteSpace: 'pre-wrap',
                              backgroundColor: 'grey.100',
                              p: 1,
                              borderRadius: 1,
                              mb: 2,
                              maxHeight: 200,
                              overflow: 'auto',
                            }}
                          >
                            {error.stack}
                          </Typography>
                        </>
                      )}

                      {errorInfo.componentStack && (
                        <>
                          <Typography variant="subtitle2" color="error" gutterBottom>
                            Component Stack:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ 
                              fontFamily: 'monospace',
                              whiteSpace: 'pre-wrap',
                              backgroundColor: 'grey.100',
                              p: 1,
                              borderRadius: 1,
                              maxHeight: 200,
                              overflow: 'auto',
                            }}
                          >
                            {errorInfo.componentStack}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
            </CardContent>
          </Card>
        </Box>
      );
    }

    // If no error, render children normally
    return this.props.children;
  }
}

// Higher-order component for functional components
export function withErrorBoundary(Component, errorBoundaryProps = {}) {
  return function WrappedComponent(props) {
    return (
      <ErrorBoundary {...errorBoundaryProps}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
}

// Hook for error handling in functional components
export function useErrorHandler() {
  const [error, setError] = React.useState(null);

  const resetError = React.useCallback(() => {
    setError(null);
  }, []);

  const captureError = React.useCallback((error) => {
    console.error('Error captured:', error);
    setError(error);
  }, []);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return {
    captureError,
    resetError,
  };
}

export default ErrorBoundary;